/**
 * Medición del embudo de alta (GA4 + Microsoft Clarity).
 *
 * Los scripts los carga public/index.html SOLO en dashflow.com.ar y en las rutas públicas
 * (nunca en los subdominios de las empresas ni en el panel). Este módulo:
 *  - manda el page_view de cada ruta pública (gtag se configura con send_page_view: false),
 *  - corta Clarity y GA4 cuando la SPA navega a una ruta privada (y los reanuda al volver),
 *  - expone trackFunnel() para los eventos Lead / CompleteRegistration / StartTrial.
 * Si los scripts no están cargados (otro host, adblock), todo es no-op.
 */

export const GA4_ID = 'G-5393WYXZ3R';
const TRACKED_HOST = 'dashflow.com.ar';

// Misma lista que public/index.html (más /ayuda/*, ver isPublicTrackedPath). /reset-password queda afuera: lleva el token en la URL.
export const PUBLIC_TRACKED_PATHS = [
  '/',
  '/precios',
  '/promo',
  '/register',
  '/signup-wa',
  '/soporte',
  '/privacidad',
  '/terminos',
  '/login',
  '/forgot-password',
];

export type FunnelEvent = 'Lead' | 'CompleteRegistration' | 'StartTrial';

type Gtag = (...args: unknown[]) => void;
type Clarity = (...args: unknown[]) => void;

const w = window as unknown as {
  gtag?: Gtag;
  clarity?: Clarity;
  [key: string]: unknown;
};

const isTrackedHost = () => window.location.hostname === TRACKED_HOST;

export const isPublicTrackedPath = (pathname: string) => {
  const path = pathname.replace(/\/+$/, '') || '/';
  // Centro de ayuda: /ayuda y todas sus lecciones.
  return PUBLIC_TRACKED_PATHS.includes(path) || path === '/ayuda' || path.startsWith('/ayuda/');
};

let clarityStopped = false;

/** Se llama en cada cambio de ruta de la SPA. */
export const syncTrackingWithRoute = (pathname: string) => {
  if (!isTrackedHost()) return;
  if (isPublicTrackedPath(pathname)) {
    w[`ga-disable-${GA4_ID}`] = false;
    if (clarityStopped) {
      w.clarity?.('start');
      clarityStopped = false;
    }
    w.gtag?.('event', 'page_view', {
      send_to: GA4_ID,
      page_location: window.location.origin + pathname,
      page_title: document.title,
    });
  } else {
    // Panel privado: no se graba ni se mide.
    w[`ga-disable-${GA4_ID}`] = true;
    if (w.clarity && !clarityStopped) {
      w.clarity('stop');
      clarityStopped = true;
    }
  }
};

/**
 * Manda los eventos a Clarity y GA4. Resuelve cuando GA4 confirma el envío (o a los 1000 ms),
 * para poder navegar al panel —donde se apaga GA4— sin perder el hit.
 */
export const trackFunnel = (...events: FunnelEvent[]): Promise<void> => {
  if (!isTrackedHost() || events.length === 0) return Promise.resolve();
  const gtag = w.gtag;
  events.forEach((name) => w.clarity?.('event', name));
  if (!gtag) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let pending = events.length;
    const timer = window.setTimeout(resolve, 1000);
    const done = () => {
      pending -= 1;
      if (pending <= 0) {
        window.clearTimeout(timer);
        resolve();
      }
    };
    events.forEach((name) =>
      gtag('event', name, { send_to: GA4_ID, transport_type: 'beacon', event_callback: done }),
    );
  });
};
